import React, { useState } from 'react'
import { useT } from '../../lib/i18n'
import { useQuery } from '../../lib/api'

interface Task {
  id: string; title: string; status: 'done' | 'in_progress' | 'blocked' | 'todo'
  priority?: string; sprint?: string; agent?: string
}
interface Sprint {
  name: string; total: number; done: number; inProgress: number
  blocked: number; todo: number; percent: number
}
interface ProjectProgress {
  projectId: string; label?: string; globalPercent: number; totalTasks: number
  doneTasks: number; inProgressTasks: number; blockedTasks: number; todoTasks: number
  sprints: Sprint[]; tasks: Task[]; lastUpdated: string | null; source: string
}

/* ── Couleurs par statut ─────────────────────────────────────── */
const STATUS_COLOR: Record<string, string> = {
  done:        'var(--health-good)',
  in_progress: 'var(--accent)',
  blocked:     'var(--health-crit)',
  todo:        'var(--text-dim)',
}
const STATUS_ICON: Record<string, string> = {
  done: '✅', in_progress: '🔄', blocked: '❌', todo: '⬜',
}
const STATUS_LABEL: Record<string, string> = {
  done: 'Terminé', in_progress: 'En cours', blocked: 'Bloqué', todo: 'À faire',
}

/* ── Barre de progression ─────────────────────────────────────── */
function ProgressBar({ percent, color = 'var(--accent)', height = 7 }: {
  percent: number; color?: string; height?: number
}) {
  return (
    <div className="progress-track" style={{ height }}>
      <div
        className="progress-fill"
        style={{
          width: `${Math.max(percent > 0 ? 2 : 0, percent)}%`,
          background: percent === 100 ? 'var(--health-good)' : color,
        }}
      />
    </div>
  )
}

/* ── Carte projet dans la sidebar ────────────────────────────── */
function ProjectCard({ proj, isSelected, onClick, index }: {
  proj: ProjectProgress; isSelected: boolean; onClick: () => void; index: number
}) {
  const pct   = proj.globalPercent
  const color = pct === 100 ? 'var(--health-good)' : pct >= 60 ? 'var(--accent)' : pct >= 30 ? 'var(--health-warn)' : 'var(--health-crit)'
  const name  = proj.label ?? proj.projectId

  return (
    <div
      onClick={onClick}
      className="animate-in"
      style={{
        padding: '12px 14px',
        background: isSelected ? 'var(--accent-dim)' : 'var(--surface)',
        border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--radius)',
        cursor: 'pointer',
        transition: 'all 150ms',
        animationDelay: `${index * 0.05}s`,
        boxShadow: isSelected ? `0 0 0 1px var(--accent)` : 'none',
      }}
      onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-2)' }}
      onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)' }}
    >
      {/* Nom + % */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
        <span style={{
          fontSize: 'var(--text-sm)',
          fontWeight: 600,
          color: isSelected ? 'var(--accent)' : 'var(--text)',
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {name}
        </span>
        <span style={{
          fontSize: 'var(--text-md)',
          fontFamily: 'var(--font-mono)',
          fontWeight: 800,
          color,
          flexShrink: 0,
        }}>
          {pct}%
        </span>
      </div>

      {/* Barre progression */}
      <ProgressBar percent={pct} color={color} height={6} />

      {/* Compteurs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '4px',
        marginTop: '8px',
      }}>
        {[
          { icon: '✅', count: proj.doneTasks,       color: 'var(--health-good)' },
          { icon: '🔄', count: proj.inProgressTasks, color: 'var(--accent)' },
          { icon: '❌', count: proj.blockedTasks,     color: 'var(--health-crit)' },
          { icon: '⬜', count: proj.todoTasks,        color: 'var(--text-dim)' },
        ].map(({ icon, count, color: c }) => (
          <div key={icon} style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1px',
            opacity: count === 0 ? 0.35 : 1,
          }}>
            <span style={{ fontSize: 11 }}>{icon}</span>
            <span style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-mono)', fontWeight: 700, color: c }}>
              {count}
            </span>
          </div>
        ))}
      </div>

      {/* Total */}
      <div style={{ fontSize: 10, color: 'var(--text-dim)', textAlign: 'right', marginTop: '4px', fontFamily: 'var(--font-mono)' }}>
        {proj.totalTasks} tâches
      </div>
    </div>
  )
}

/* ── Sprint ───────────────────────────────────────────────────── */
function SprintSection({ sprint, index }: { sprint: Sprint; index: number }) {
  const color = sprint.percent === 100 ? 'var(--health-good)' : sprint.percent >= 50 ? 'var(--accent)' : 'var(--health-warn)'
  return (
    <div className="animate-in" style={{ marginBottom: '14px', animationDelay: `${index * 0.05}s` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)', flex: 1, lineHeight: 1.3 }}>
          {sprint.name}
        </span>
        <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', color, fontWeight: 700, flexShrink: 0 }}>
          {sprint.percent}%
        </span>
      </div>
      <ProgressBar percent={sprint.percent} color={color} height={5} />
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: 10, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', flexWrap: 'wrap' }}>
        {sprint.done > 0      && <span style={{ color: 'var(--health-good)' }}>✅ {sprint.done}</span>}
        {sprint.inProgress > 0 && <span style={{ color: 'var(--accent)' }}>🔄 {sprint.inProgress}</span>}
        {sprint.blocked > 0   && <span style={{ color: 'var(--health-crit)' }}>❌ {sprint.blocked}</span>}
        {sprint.todo > 0      && <span style={{ color: 'var(--text-dim)' }}>⬜ {sprint.todo}</span>}
        <span style={{ marginLeft: 'auto', color: 'var(--text-dim)' }}>{sprint.total} total</span>
      </div>
    </div>
  )
}

/* ── Ligne tâche ──────────────────────────────────────────────── */
function TaskRow({ task, index }: { task: Task; index: number }) {
  const color  = STATUS_COLOR[task.status] ?? 'var(--text-dim)'
  const isDone = task.status === 'done'

  return (
    <div
      className="animate-in"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '10px',
        padding: '9px 12px',
        borderBottom: '1px solid var(--border)',
        opacity: isDone ? 0.55 : 1,
        transition: 'background 100ms',
        animationDelay: `${index * 0.025}s`,
      }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)'}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = ''}
    >
      {/* Icône statut */}
      <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>
        {STATUS_ICON[task.status]}
      </span>

      {/* Contenu */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'var(--text-base)',
          color: isDone ? 'var(--text-muted)' : 'var(--text)',
          textDecoration: isDone ? 'line-through' : 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: isDone ? 400 : 500,
        }}>
          {task.title}
        </div>
        {(task.priority || task.agent || task.sprint) && (
          <div style={{ display: 'flex', gap: '5px', marginTop: '3px', flexWrap: 'wrap' }}>
            {task.priority && (
              <span style={{
                fontSize: 10,
                background: 'var(--accent-dim)',
                color: 'var(--accent)',
                padding: '1px 6px',
                borderRadius: '4px',
                fontFamily: 'var(--font-mono)',
                fontWeight: 600,
              }}>
                {task.priority}
              </span>
            )}
            {task.sprint && (
              <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                {task.sprint}
              </span>
            )}
            {task.agent && (
              <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                · {task.agent}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Badge statut */}
      <span style={{
        fontSize: 10,
        color,
        flexShrink: 0,
        fontFamily: 'var(--font-mono)',
        fontWeight: 700,
        background: `color-mix(in srgb, ${color} 12%, transparent)`,
        padding: '2px 7px',
        borderRadius: '4px',
        marginTop: 1,
        whiteSpace: 'nowrap',
      }}>
        {STATUS_LABEL[task.status] ?? task.status}
      </span>
    </div>
  )
}

/* ── Bouton filtre ────────────────────────────────────────────── */
function FilterBtn({ active, onClick, icon, label, count }: {
  active: boolean; onClick: () => void; icon: string; label: string; count: number
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        padding: '5px 11px',
        border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
        borderRadius: 'var(--radius)',
        background: active ? 'var(--accent-dim)' : 'transparent',
        color: active ? 'var(--accent)' : 'var(--text-muted)',
        cursor: 'pointer',
        fontSize: 'var(--text-xs)',
        fontFamily: 'var(--font-sans)',
        fontWeight: active ? 600 : 400,
        transition: 'all 120ms',
        whiteSpace: 'nowrap',
      }}
    >
      <span>{icon}</span>
      <span>{label}</span>
      <span style={{
        background: active ? 'var(--accent)' : 'var(--surface-2)',
        color: active ? 'white' : 'var(--text-dim)',
        borderRadius: '999px',
        padding: '0 5px',
        fontSize: 10,
        fontFamily: 'var(--font-mono)',
        fontWeight: 700,
        minWidth: 18,
        textAlign: 'center',
      }}>
        {count}
      </span>
    </button>
  )
}

/* ── Vue principale Progress ──────────────────────────────────── */
export default function Progress() {
  const t = useT()
  const [selected, setSelected] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'in_progress' | 'todo' | 'blocked' | 'done'>('all')

  const { data, loading } = useQuery<{ projects: ProjectProgress[] }>('/progress', 60_000)
  const projects = (data?.projects ?? [])
    .filter(Boolean)
    .sort((a, b) => b.globalPercent - a.globalPercent) as ProjectProgress[]

  const selectedProj = selected ? projects.find(p => p.projectId === selected) : null
  const displayProj  = selectedProj ?? (
    projects.length > 0
      ? projects.reduce((a, b) => b.inProgressTasks > a.inProgressTasks ? b : a, projects[0]!)
      : null
  )

  const filteredTasks = (displayProj?.tasks ?? []).filter(t =>
    filter === 'all' ? true : t.status === filter
  )

  /* KPIs globaux */
  const totalDone  = projects.reduce((s, p) => s + p.doneTasks, 0)
  const totalAll   = projects.reduce((s, p) => s + p.totalTasks, 0)
  const globalPct  = totalAll > 0 ? Math.round(totalDone / totalAll * 100) : 0
  const inProgress = projects.reduce((s, p) => s + p.inProgressTasks, 0)
  const blocked    = projects.reduce((s, p) => s + p.blockedTasks, 0)
  const globalColor = globalPct === 100 ? 'var(--health-good)' : globalPct >= 60 ? 'var(--accent)' : 'var(--health-warn)'

  if (loading && !data) return (
    <div style={{ padding: '32px', color: 'var(--text-muted)', fontSize: 'var(--text-base)' }}>
      {t('loading')}
    </div>
  )

  if (projects.length === 0) return (
    <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-base)' }}>
      Aucun fichier TASKS.md trouvé dans les projets configurés.<br />
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-dim)', display: 'block', marginTop: '8px' }}>
        Créer un <code style={{ background: 'var(--surface-2)', padding: '1px 6px', borderRadius: '4px' }}>TASKS.md</code> dans <code style={{ background: 'var(--surface-2)', padding: '1px 6px', borderRadius: '4px' }}>.claude-agents/</code> de chaque projet.
      </span>
    </div>
  )

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden', gap: '0' }}>

      {/* ── Colonne gauche — liste projets ─────────────────────── */}
      <div style={{
        width: '240px',
        flexShrink: 0,
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* KPI global */}
        <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '6px' }}>
            <span style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 800,
              fontFamily: 'var(--font-mono)',
              color: globalColor,
              letterSpacing: '-0.03em',
              lineHeight: 1,
            }}>
              {globalPct}%
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
              {totalDone}/{totalAll}
            </span>
          </div>
          <ProgressBar percent={globalPct} color={globalColor} height={8} />
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px', fontSize: 11, fontFamily: 'var(--font-mono)', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--health-good)', fontWeight: 700 }}>✅ {totalDone}</span>
            {inProgress > 0 && <span style={{ color: 'var(--accent)', fontWeight: 700 }}>🔄 {inProgress}</span>}
            {blocked > 0   && <span style={{ color: 'var(--health-crit)', fontWeight: 700 }}>❌ {blocked}</span>}
          </div>
        </div>

        {/* Liste projets */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '10px 10px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
            {projects.map((p, i) => (
              <ProjectCard
                key={p.projectId}
                proj={p}
                index={i}
                isSelected={displayProj?.projectId === p.projectId}
                onClick={() => setSelected(p.projectId === selected ? null : p.projectId)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Colonne droite — détail projet ─────────────────────── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {displayProj ? (
          <>
            {/* Header projet */}
            <div style={{
              padding: '12px 18px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexShrink: 0,
              background: 'var(--surface)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayProj.label ?? displayProj.projectId}
                </div>
                {displayProj.lastUpdated && (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', marginTop: '1px' }}>
                    Mis à jour {new Date(displayProj.lastUpdated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{
                  fontSize: 'var(--text-xl)',
                  fontWeight: 800,
                  fontFamily: 'var(--font-mono)',
                  color: displayProj.globalPercent === 100 ? 'var(--health-good)' : 'var(--accent)',
                  letterSpacing: '-0.02em',
                  lineHeight: 1,
                }}>
                  {displayProj.globalPercent}%
                </div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: '3px' }}>
                  {displayProj.doneTasks} / {displayProj.totalTasks} tâches
                </div>
              </div>
            </div>

            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

              {/* Sprints */}
              {displayProj.sprints.length > 0 && (
                <div style={{
                  width: '230px',
                  flexShrink: 0,
                  borderRight: '1px solid var(--border)',
                  overflowY: 'auto',
                  minHeight: 0,
                  padding: '14px 14px',
                }}>
                  <div style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 700,
                    color: 'var(--text-dim)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: '12px',
                  }}>
                    Sprints · {displayProj.sprints.length}
                  </div>
                  {displayProj.sprints.map((s, i) => (
                    <SprintSection key={i} sprint={s} index={i} />
                  ))}
                </div>
              )}

              {/* Tâches */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
                {/* Filtres */}
                <div style={{
                  padding: '10px 14px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  gap: '6px',
                  flexShrink: 0,
                  flexWrap: 'wrap',
                  background: 'var(--bg-2)',
                }}>
                  <FilterBtn active={filter === 'all'}         onClick={() => setFilter('all')}         icon="📋" label="Toutes"    count={displayProj.totalTasks} />
                  <FilterBtn active={filter === 'in_progress'} onClick={() => setFilter('in_progress')} icon="🔄" label="En cours"  count={displayProj.inProgressTasks} />
                  <FilterBtn active={filter === 'todo'}        onClick={() => setFilter('todo')}        icon="⬜" label="À faire"   count={displayProj.todoTasks} />
                  {displayProj.blockedTasks > 0 && (
                    <FilterBtn active={filter === 'blocked'} onClick={() => setFilter('blocked')} icon="❌" label="Bloqué" count={displayProj.blockedTasks} />
                  )}
                  <FilterBtn active={filter === 'done'} onClick={() => setFilter('done')} icon="✅" label="Terminé" count={displayProj.doneTasks} />
                </div>

                {/* Liste tâches */}
                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  {filteredTasks.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-base)' }}>
                      {t('no_data')}
                    </div>
                  ) : (
                    filteredTasks.map((task, i) => <TaskRow key={task.id} task={task} index={i} />)
                  )}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-base)' }}>
            Sélectionnez un projet pour voir l'avancement détaillé
          </div>
        )}
      </div>
    </div>
  )
}
